import { tokenManager } from './TokenManager.js';
import { storage } from './storage.js';
import { applyTranslationsToContainer, getCurrentLanguage, loadTranslations, interpolate, getByPath } from '../translations.js';
import {
  hydratePricingFromPlans,
  resolveNextUpgradeUiPlan,
  formatTierDisplayName,
  fetchMembershipPlans,
  getPlanByTier,
  formatPlanPrice,
} from './pricingPlans.js';
import { fetchUserCreatedAt } from './userProfileApi.js';
import { fetchCache } from './fetchCache.js';
import { WORKER_URL, CDN_URL, STRIPE_WORKER_URL, SFW_ORIGIN, isSfwSite } from '../config.js';
import { startCryptoCheckout } from './cryptoCheckout.js';
import { bindCryptoPricing, cryptoPackTitle, loadCryptoSpendEstimate, renderCryptoPricing } from './cryptoPricing.js';
import { getNsfwEnabled } from './userPrefs.js';
import { PREMIUM_STYLE_TIERS } from '../hangout/ui/constants.js';

const STARTER_SUBSCRIBE_SELECTOR = '.quota-starter-subscribe-btn, .starter-tier-subscribe-btn';
const TRIAL_OFFER_TEMPLATES = new Set([
  '/components/modals/trial-offer-modal.html',
  '/components/modals/trial-offer-only-modal.html',
  '/components/modals/premium-pass-trial-modal.html',
]);

const loadCSS = (href) => new Promise((resolve) => {
  try {
    if (document.querySelector(`link[href="${href}"]`)) return resolve(true);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve(true);
    link.onerror = () => resolve(false);
    document.head.appendChild(link);
  } catch {
    resolve(false);
  }
});

const loadTemplate = async (url) => {
  try {
    return await fetchCache.fetchText(url);
  } catch {
    return null;
  }
};

const isLoggedInUser = () =>
  tokenManager.isAuthenticated() && storage.get('userData')?.isLoggedIn === true;

const getCheckoutUserId = async () => {
  try {
    const token = await tokenManager.getValidAccessToken();
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).sub;
  } catch {
    return null;
  }
};

const updateTrialOfferModalContent = (modalContainer) => {
  if (!isSfwSite()) return;
  const stripeConfig = /** @type {any} */ (window).STRIPE_CONFIG;
  const paymentLinks = stripeConfig?.PAYMENT_LINKS;
  const trialLink = paymentLinks?.starter_paid_trial || paymentLinks?.trial || '';

  const trialButton = modalContainer.querySelector('.trial-offer-button');
  if (trialButton) trialButton.setAttribute('data-link', trialLink);

  modalContainer.querySelectorAll(STARTER_SUBSCRIBE_SELECTOR).forEach((button) => {
    if (!trialLink) return;
    button.setAttribute('data-monthly-link', trialLink);
  });
};

const createStarterPaidTrialCheckoutSession = async ({ userId, userEmail }) => {
  if (!isSfwSite()) throw new Error('Card checkout is available on kissandra.ai');
  const token = await tokenManager.getValidAccessToken();
  if (!token) throw new Error('Authentication required. Please log in and try again.');

  const response = await fetch(`${STRIPE_WORKER_URL}/create-starter-paid-trial-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      userId,
      userEmail,
      successUrl: `${SFW_ORIGIN}/account.html?checkout=starter_paid_trial`,
      cancelUrl: `${SFW_ORIGIN}/pricing.html`
    })
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(payload?.error || payload?.details || 'Could not create checkout session');
  }
  if (!payload?.checkoutUrl) throw new Error('Checkout URL missing from Stripe worker response');

  return payload.checkoutUrl;
};

export const handlePaidTrialCheckoutClick = async (button, loginAlert) => {
  const userData = storage.get('userData') || {};
  const userEmail = userData?.email;
  const userId = await getCheckoutUserId();

  if (!userId || !userEmail) {
    alert(loginAlert);
    return;
  }

  const originalText = button.textContent;
  button.textContent = 'Redirecting to payment...';
  button.disabled = true;
  try {
    window.location.href = await createStarterPaidTrialCheckoutSession({ userId, userEmail });
  } catch (error) {
    alert(error.message || 'Could not start checkout. Please try again.');
    button.textContent = originalText;
    button.disabled = false;
  }
};

const formatTierLabel = (tier) => {
  const normalizedTier = String(tier || 'free').trim();
  if (!normalizedTier) return 'Free';
  return normalizedTier
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .map((word) => word ? `${word[0].toUpperCase()}${word.slice(1)}` : '')
    .join(' ');
};

const resolveVoiceModalCharacterName = (characterId) => {
  const currentHangoutConfig = storage.get('currentHangoutConfig') || {};
  const allCharacters = Array.isArray(currentHangoutConfig?.allCharacters)
    ? currentHangoutConfig.allCharacters
    : [];

  if (characterId != null) {
    const matchingCharacter = allCharacters.find((character) => (
      String(character?.id) === String(characterId) ||
      String(character?.characterId) === String(characterId)
    ));
    if (matchingCharacter?.name) return matchingCharacter.name;
  }

  return allCharacters[0]?.name || currentHangoutConfig?.characterName || 'them';
};

const resolveHangoutCharacterId = () => {
  const hangoutConfig = storage.get('currentHangoutConfig') || {};
  return hangoutConfig.characterId
    || hangoutConfig.allCharacters?.[0]?.id
    || hangoutConfig.allCharacters?.[0]?.characterId
    || null;
};

const resolveUserCreatedAt = async () => {
  const userData = storage.get('userData') || {};
  if (userData.created_at) return userData.created_at;

  const token = await tokenManager.getValidAccessToken();
  if (!token) return null;

  const createdAt = await fetchUserCreatedAt({ token });
  if (!createdAt) return null;

  storage.set('userData', { ...userData, created_at: createdAt });
  return createdAt;
};

const clearQuotaCountdown = (modalContainer) => {
  if (!modalContainer.dataset.countdownInterval) return;
  clearInterval(parseInt(modalContainer.dataset.countdownInterval, 10));
  delete modalContainer.dataset.countdownInterval;
};

const startQuotaCountdown = async (modalContainer) => {
  try {
    const createdAt = await resolveUserCreatedAt();
    const countdownElement = createdAt && modalContainer.querySelector('.quota-countdown-timer');
    if (!countdownElement) return;

    const updateCountdown = () => {
      const remaining = new Date(createdAt).getTime() + (48 * 60 * 60 * 1000) - Date.now();
      if (remaining <= 0) {
        countdownElement.textContent = '00:00:00';
        return;
      }
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
      countdownElement.textContent = [hours, minutes, seconds]
        .map((n) => String(n).padStart(2, '0'))
        .join(':');
    };

    updateCountdown();
    const countdownInterval = setInterval(updateCountdown, 1000);
    modalContainer.dataset.countdownInterval = String(countdownInterval);
  } catch (error) {
    console.error('[Quota Modal] Failed to setup countdown:', error);
  }
};

const setupTrialOfferModalEvents = (modalContainer, { onClose, afterRemove } = {}) => {
  const closeModal = () => {
    const allowedAtRaw = modalContainer.dataset.closeAllowedAt;
    if (allowedAtRaw !== undefined && allowedAtRaw !== '') {
      const allowedAt = parseInt(allowedAtRaw, 10);
      if (!Number.isNaN(allowedAt) && Date.now() < allowedAt) return;
    }
    modalContainer.classList.remove('show');
    document.removeEventListener('keydown', handleEscape);
    modalContainer._gridResizeObserver?.disconnect();
    onClose?.();
    setTimeout(() => {
      modalContainer.remove();
      afterRemove?.();
    }, 300);
  };

  const handleEscape = (e) => {
    if (e.key === 'Escape') closeModal();
  };

  modalContainer.querySelector('.quota-pricing-close')?.addEventListener('click', closeModal);
  modalContainer.addEventListener('click', (e) => {
    if (e.target === modalContainer) closeModal();
  });
  document.addEventListener('keydown', handleEscape);

  if (!isSfwSite()) return;

  const trialButton = modalContainer.querySelector('.trial-offer-button');
  trialButton?.addEventListener('click', async (e) => {
    e.preventDefault();
    await handlePaidTrialCheckoutClick(trialButton, 'Please log in to start your trial');
  });

  const starterButton = modalContainer.querySelector(STARTER_SUBSCRIBE_SELECTOR);
  starterButton?.addEventListener('click', async (e) => {
    e.preventDefault();
    await handlePaidTrialCheckoutClick(starterButton, 'Please log in to subscribe');
  });
};

const fetchTrialSprites = async (endpoint) => {
  try {
    const data = await fetchCache.fetchJSON(endpoint, { headers: { Accept: 'application/json' } });
    return Array.isArray(data?.sprites) ? data.sprites : [];
  } catch {
    return [];
  }
};

const initTrialImageGrid = async (container) => {
  try {
    const viewport = container.querySelector('.trial-grid-viewport');
    const track = container.querySelector('.trial-grid-track');
    if (!viewport || !track) return;
    if (viewport.dataset.initialized === '1') return;
    viewport.dataset.initialized = '1';

    const isLowMessagesModal = container.classList.contains('low-messages-modal');
    const modalType = isLowMessagesModal ? 'low_messages' : 'trial';
    const currentCharacterId = resolveHangoutCharacterId();
    const query = currentCharacterId
      ? `character_id=${encodeURIComponent(currentCharacterId)}&mixed=true&modal_type=${modalType}`
      : `mixed=true&modal_type=${modalType}`;
    const sfwSuffix = getNsfwEnabled() ? '' : '&sfw=1';
    let sprites = await fetchTrialSprites(`${WORKER_URL}/data/trial/sprites?${query}${sfwSuffix}`);
    if (!sprites.length) {
      sprites = await fetchTrialSprites(`${WORKER_URL}/data/trial/sprites${sfwSuffix ? `?sfw=1` : ''}`);
    }
    if (!sprites.length) return;

    const shuffledSprites = [...sprites];
    for (let i = shuffledSprites.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledSprites[i], shuffledSprites[j]] = [shuffledSprites[j], shuffledSprites[i]];
    }

    const pairs = [];
    for (let i = 0; i < shuffledSprites.length; i += 2) {
      pairs.push([shuffledSprites[i], shuffledSprites[(i + 1) % shuffledSprites.length]]);
    }
    if (pairs.length < 4) {
      const needed = 4 - pairs.length;
      for (let i = 0; i < needed; i++) pairs.push(pairs[i % pairs.length]);
    }

    const originalColumns = Math.min(12, pairs.length);
    track.innerHTML = '';

    const buildColumns = (count) => {
      for (let i = 0; i < count; i++) {
        const [sprite1, sprite2] = pairs[i % pairs.length];
        const col = document.createElement('div');
        col.className = 'trial-grid-column';
        const c1 = document.createElement('div');
        c1.className = 'trial-grid-cell';
        const c2 = document.createElement('div');
        c2.className = 'trial-grid-cell';
        const img1 = document.createElement('img');
        img1.src = `${CDN_URL}${sprite1?.imageUrl}`;
        img1.alt = 'Preview';
        img1.loading = 'lazy';
        const img2 = document.createElement('img');
        img2.src = `${CDN_URL}${sprite2?.imageUrl}`;
        img2.alt = 'Preview';
        img2.loading = 'lazy';
        if (!isLowMessagesModal) {
          if (sprite1?.shouldBlur) img1.style.filter = 'blur(6px)';
          if (sprite2?.shouldBlur) img2.style.filter = 'blur(6px)';
        }
        c1.appendChild(img1);
        c2.appendChild(img2);
        col.appendChild(c1);
        col.appendChild(c2);
        track.appendChild(col);
      }
    };
    buildColumns(originalColumns);
    buildColumns(originalColumns);

    const computeAndApply = () => {
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || '10');
      const availableWidth = viewport.clientWidth - gap;
      const isPremiumPassBg = container.classList.contains('premium-pass-bg');
      let colW;
      if (isPremiumPassBg) {
        const rowH = (viewport.clientHeight - gap) / 2;
        const portraitColW = Math.floor(rowH * (3 / 4));
        const twoColW = Math.floor(availableWidth / 2);
        colW = Math.max(80, Math.min(twoColW, portraitColW));
      } else {
        colW = Math.max(80, Math.floor(availableWidth / 2));
      }
      track.style.setProperty('--colW', `${colW}px`);
      track.style.setProperty('--gap', `${gap}px`);
      track.style.setProperty('--loopW', `${originalColumns * (colW + gap)}px`);
      track.style.setProperty('--duration', `${Math.max(24, Math.floor(originalColumns * 6))}s`);
    };
    computeAndApply();

    const resizeObserver = new ResizeObserver(computeAndApply);
    resizeObserver.observe(viewport);
    container._gridResizeObserver = resizeObserver;

    requestAnimationFrame(() => {
      setTimeout(() => {
        computeAndApply();
        viewport.classList.add('ready');
      }, 100);
    });
  } catch (e) {
    console.error('initTrialImageGrid error:', e);
  }
};

export const initializeTrialImageGridFor = (container) => initTrialImageGrid(container);

const showModalEl = (modalContainer, closeDelayMs) => {
  requestAnimationFrame(() => {
    modalContainer.style.display = 'flex';
    modalContainer.classList.add('show');
    if (closeDelayMs) {
      modalContainer.dataset.closeAllowedAt = String(Date.now() + closeDelayMs);
    }
  });
};

const mountPricingModal = async ({
  template,
  prepare,
  afterMount,
  initGrid = true,
  delayGrid = false,
  closeDelayMs,
}) => {
  if (!isSfwSite() && TRIAL_OFFER_TEMPLATES.has(template)) {
    await showCryptoPacksModal();
    return null;
  }
  await loadCSS('/css/modals/pricing.css');
  const modalHTML = await loadTemplate(template);
  if (!modalHTML) {
    console.error('Failed to load modal template:', template);
    return null;
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = modalHTML;
  const modalContainer = [...tempDiv.children].find((node) => node.tagName !== 'SCRIPT' && node.tagName !== 'STYLE') ?? null;
  if (!modalContainer) return null;

  prepare?.(modalContainer);
  document.body.appendChild(modalContainer);
  updateTrialOfferModalContent(modalContainer);

  const lang = getCurrentLanguage();
  await loadTranslations('pricing', lang);
  applyTranslationsToContainer(modalContainer, 'pricing', lang);
  await hydratePricingFromPlans(modalContainer, lang);
  await afterMount?.(modalContainer, lang);
  await adaptFullSiteCheckout(modalContainer);

  if (delayGrid) {
    showModalEl(modalContainer, closeDelayMs);
    setTimeout(() => initTrialImageGrid(modalContainer), 350);
  } else {
    if (initGrid) await initTrialImageGrid(modalContainer);
    showModalEl(modalContainer, closeDelayMs);
  }
  return modalContainer;
};

const ensureLoginModal = async () => {
  const controller = window.loginModalController || (await import('../components/LoginModalController.js')).default;
  await controller.init();
  controller.show();
};

export const showPremiumPassTrialModal = async () => {
  if (!isSfwSite()) {
    await showCryptoPacksModal();
    return;
  }
  await mountPricingModal({
    template: '/components/modals/premium-pass-trial-modal.html',
    initGrid: false,
    afterMount: (modalContainer) => {
      setupTrialOfferModalEvents(modalContainer);
      const tier = String(storage.get('userData')?.current_tier || 'free').trim().toLowerCase();
      if (!tier || tier === 'free') return;
      const button = modalContainer.querySelector('.trial-offer-button');
      if (!button) return;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    },
  });
};

export const showTrialOfferModal = async () => {
  try {
    if (!isSfwSite()) {
      await showCryptoPacksModal();
      return;
    }
    if (isLoggedInUser()) {
      await mountPricingModal({
        template: '/components/modals/trial-offer-modal.html',
        afterMount: (modalContainer) => setupTrialOfferModalEvents(modalContainer),
      });
      return;
    }

    if (!document.querySelector('link[href="/css/modals/pricing-trial-auth.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/css/modals/pricing-trial-auth.css';
      document.head.appendChild(link);
    }
    await ensureLoginModal();
  } catch (error) {
    console.error('showTrialOfferModal error:', error);
  }
};

export const showVoiceAccessPricingModal = async ({ currentTier = 'free', characterId = null } = {}) => {
  try {
    if (!isLoggedInUser()) {
      await ensureLoginModal();
      return;
    }

    await mountPricingModal({
      template: '/components/modals/pricing-modal.html',
      initGrid: false,
      afterMount: (modalContainer) => {
        wirePricingModalCheckout(modalContainer);
        const titleElement = modalContainer.querySelector('#pricing-modal-title');
        const descriptionElement = modalContainer.querySelector('#pricing-modal-description');
        const tierLabel = formatTierLabel(currentTier);
        const characterName = resolveVoiceModalCharacterName(characterId);

        if (titleElement) {
          const titleTemplate = titleElement.textContent || 'Voice feature is not available on {tier} tier';
          titleElement.textContent = titleTemplate.replace('{tier}', tierLabel);
        }
        if (descriptionElement) {
          const descriptionTemplate = descriptionElement.textContent || 'Please upgrade your membership to hear {character}\'s voice';
          descriptionElement.textContent = descriptionTemplate.replace('{character}', characterName);
        }
      },
    });
  } catch (error) {
    console.error('[Voice Access Pricing Modal] Error showing modal:', error);
  }
};

const getBrowseAccessStrings = (lang, variant) => {
  const pricingRoot =
    window.translations?.[lang]?.pricing
    ?? window.translations?.en?.pricing
    ?? {};
  const browseAccess = pricingRoot.browse_access ?? getByPath(window.translations?.en?.pricing, 'browse_access') ?? {};
  const titleKey = variant === 'trial' ? 'title' : 'starter_title';
  const subtitleKey = variant === 'trial' ? 'subtitle' : 'starter_subtitle';
  return {
    title: browseAccess[titleKey] ?? '',
    subtitle: browseAccess[subtitleKey] ?? '',
  };
};

const applyBrowseAccessCopy = async (modalContainer, variant, lang) => {
  const plans = await fetchMembershipPlans();
  const trialPlan = getPlanByTier('trial', plans);
  const starterPlan = getPlanByTier('starter', plans);
  const vars = {
    trial_price: formatPlanPrice(trialPlan?.monthly_price_usd ?? 0, lang),
    starter_price: formatPlanPrice(starterPlan?.monthly_price_usd ?? 0, lang),
  };

  const { title, subtitle } = getBrowseAccessStrings(lang, variant);
  const titleEl = modalContainer.querySelector('.trial-offer-title, .quota-exhausted-custom-title');
  if (titleEl && title) {
    titleEl.textContent = interpolate(title, vars);
  }

  let subtitleEl = modalContainer.querySelector('.trial-price');
  if (!subtitleEl && subtitle && titleEl) {
    subtitleEl = document.createElement('p');
    subtitleEl.className = 'browse-access-subtitle trial-price';
    titleEl.insertAdjacentElement('afterend', subtitleEl);
  }
  if (subtitleEl && subtitle) {
    subtitleEl.textContent = interpolate(subtitle, vars);
  }

  modalContainer.querySelector('.pricing-messages-disclaimer')?.remove();
};

const getQuotaUpsellStrings = (lang, feature) => {
  const pricingRoot =
    window.translations?.[lang]?.pricing
    ?? window.translations?.en?.pricing
    ?? {};
  const upsell = pricingRoot.quota_upsell ?? getByPath(window.translations?.en?.pricing, 'quota_upsell') ?? {};
  const subtitleByFeature = {
    browse: 'browse_subtitle',
    messages: 'messages_subtitle',
    voice: 'voice_subtitle',
    image: 'image_subtitle',
    group: 'group_subtitle',
  };
  const subtitleKey = subtitleByFeature[feature] || 'subtitle';
  const englishUpsell = getByPath(window.translations?.en?.pricing, 'quota_upsell') ?? {};
  return {
    title: upsell.title ?? '',
    subtitle: upsell[subtitleKey] ?? englishUpsell[subtitleKey] ?? upsell.subtitle ?? '',
  };
};

const applyModalTitleSubtitle = (modalContainer, { title, subtitle, removeDisclaimer = false } = {}) => {
  const titleEl = modalContainer.querySelector('#pricing-modal-title, .trial-offer-title, .quota-exhausted-custom-title');
  const descriptionEl = modalContainer.querySelector('#pricing-modal-description');

  if (titleEl && title) titleEl.textContent = title;

  if (descriptionEl && subtitle) {
    descriptionEl.textContent = subtitle;
    if (removeDisclaimer) modalContainer.querySelector('.pricing-messages-disclaimer')?.remove();
    return;
  }

  if (!subtitle || !titleEl) return;

  let subtitleEl = modalContainer.querySelector('.quota-upsell-subtitle, .trial-price, .browse-access-subtitle');
  if (!subtitleEl) {
    subtitleEl = document.createElement('p');
    subtitleEl.className = 'quota-upsell-subtitle trial-price';
    titleEl.insertAdjacentElement('afterend', subtitleEl);
  }
  subtitleEl.textContent = subtitle;
  if (removeDisclaimer) modalContainer.querySelector('.pricing-messages-disclaimer')?.remove();
};

const applyQuotaUpsellCopy = (modalContainer, { feature, nextUiPlan, lang }) => {
  const { title, subtitle } = getQuotaUpsellStrings(lang, feature);
  const vars = { next_tier: formatTierDisplayName(nextUiPlan, lang) };
  applyModalTitleSubtitle(modalContainer, {
    title: interpolate(title, vars),
    subtitle: interpolate(subtitle, vars),
  });
};

const scrubStripeMarkup = (root) => {
  root.querySelectorAll('.powered-by-stripe').forEach((node) => node.remove());
  root.querySelectorAll('[data-monthly-link], [data-yearly-link], [data-link], a[href]').forEach((el) => {
    ['data-monthly-link', 'data-yearly-link', 'data-link'].forEach((name) => {
      const value = el.getAttribute(name) || '';
      if (value.includes('stripe.com')) el.removeAttribute(name);
    });
    if (el instanceof HTMLAnchorElement && /stripe\.com/i.test(el.href)) el.removeAttribute('href');
  });
};

const adaptFullSiteCheckout = async (modalContainer) => {
  if (isSfwSite() || !modalContainer) return;
  scrubStripeMarkup(modalContainer);
  modalContainer.querySelectorAll('.trial-disclaimer, .pricing-toggle, [data-pricing-plan="trial"]').forEach((node) => node.remove());

  const markup = renderCryptoPricing(getCurrentLanguage(), await loadCryptoSpendEstimate());
  const container = modalContainer.querySelector('#quota-pricing-container, #pricing-container');
  if (container) {
    container.outerHTML = markup;
  } else {
    const buttons = [...modalContainer.querySelectorAll('.trial-offer-button, .plan-button, .subscribe-button, .starter-tier-subscribe-btn, .quota-starter-subscribe-btn')];
    const host = buttons[0]?.parentElement;
    buttons.forEach((button) => button.remove());
    if (host) host.insertAdjacentHTML('beforeend', markup);
  }

  bindCryptoPricing(modalContainer, (button) => {
    handleCryptoPlanClick(button);
  });
  modalContainer.querySelectorAll('[data-pricing-plan="trial"]').forEach((node) => node.remove());
};

const handleCryptoPlanClick = async (button) => {
  if (!isLoggedInUser()) {
    await ensureLoginModal();
    return;
  }
  try {
    await startCryptoCheckout({ plan: button.getAttribute('data-crypto-plan'), button });
  } catch (error) {
    const { showError } = await import('./UnifiedNotificationSystem.js');
    showError(error.message || 'Could not start crypto checkout', { duration: 5000 });
  }
};

const showCryptoPacksModal = async () => {
  document.querySelector('.crypto-packs-modal')?.remove();
  await loadCSS('/css/modals/pricing.css');
  const lang = getCurrentLanguage();
  await loadTranslations('pricing', lang);
  const spend = await loadCryptoSpendEstimate();
  const root = document.createElement('div');
  root.className = 'crypto-packs-modal quota-pricing-modal-overlay';
  root.innerHTML = `
    <div class="quota-pricing-modal" role="dialog" aria-modal="true" aria-labelledby="crypto-packs-title">
      <button class="quota-pricing-close" type="button" aria-label="Close">×</button>
      <h2 id="crypto-packs-title"></h2>
      <div class="crypto-pack-host">${renderCryptoPricing(lang, spend)}</div>
    </div>`;
  const title = root.querySelector('#crypto-packs-title');
  if (title) title.textContent = cryptoPackTitle(lang);
  document.body.appendChild(root);
  bindCryptoPricing(root, (button) => {
    handleCryptoPlanClick(button);
  });
  const close = () => {
    root.classList.remove('show');
    document.removeEventListener('keydown', onKey);
    setTimeout(() => root.remove(), 300);
  };
  const onKey = (event) => {
    if (event.key === 'Escape') close();
  };
  root.querySelector('.quota-pricing-close')?.addEventListener('click', close);
  root.addEventListener('click', (event) => {
    if (event.target === root) close();
  });
  document.addEventListener('keydown', onKey);
  showModalEl(root);
  return root;
};

const wirePricingModalCheckout = (modalContainer) => {
  if (!isSfwSite()) return;
  const stripeConfig = /** @type {any} */ (window).STRIPE_CONFIG;
  const paymentLinks = stripeConfig?.PAYMENT_LINKS || {};
  const yearlyPrices = modalContainer.querySelectorAll('.yearly-price');
  const monthlyPrices = modalContainer.querySelectorAll('.price-amount');
  const monthlyBtn = modalContainer.querySelector('.monthly-btn');
  const yearlyBtn = modalContainer.querySelector('.yearly-btn');
  const planButtons = modalContainer.querySelectorAll('.plan-button');

  let billingCycle = 'monthly';

  const updateBillingCycleUI = () => {
    const isMonthly = billingCycle === 'monthly';
    monthlyBtn?.classList.toggle('active', isMonthly);
    yearlyBtn?.classList.toggle('active', !isMonthly);
    monthlyPrices.forEach((el) => { el.style.display = isMonthly ? '' : 'none'; });
    yearlyPrices.forEach((el) => { el.style.display = isMonthly ? 'none' : ''; });
  };

  planButtons.forEach((button) => {
    const plan = button.getAttribute('data-plan');
    if (!plan) return;
    button.setAttribute('data-monthly-link', paymentLinks[`${plan}_monthly`] || '');
    button.setAttribute('data-yearly-link', paymentLinks[`${plan}_yearly`] || '');
  });

  const closeModal = () => {
    modalContainer.classList.remove('show');
    document.removeEventListener('keydown', handleEscape);
    modalContainer._gridResizeObserver?.disconnect();
    setTimeout(() => { modalContainer.remove(); }, 300);
  };

  const handleEscape = (event) => {
    if (event.key !== 'Escape') return;
    closeModal();
  };

  monthlyBtn?.addEventListener('click', () => {
    billingCycle = 'monthly';
    updateBillingCycleUI();
  });

  yearlyBtn?.addEventListener('click', () => {
    billingCycle = 'yearly';
    updateBillingCycleUI();
  });

  planButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      const targetLink = billingCycle === 'yearly'
        ? button.getAttribute('data-yearly-link')
        : button.getAttribute('data-monthly-link');
      if (!targetLink) {
        alert('Checkout link unavailable right now. Please try again.');
        return;
      }
      window.location.href = targetLink;
    });
  });

  modalContainer.querySelector('.quota-pricing-close')?.addEventListener('click', closeModal);
  modalContainer.addEventListener('click', (event) => {
    if (event.target === modalContainer) closeModal();
  });
  document.addEventListener('keydown', handleEscape);

  updateBillingCycleUI();
  return { closeModal, updateBillingCycleUI };
};

const getModelAccessStrings = (lang, lane) => {
  const pricingRoot =
    window.translations?.[lang]?.pricing
    ?? window.translations?.en?.pricing
    ?? {};
  const modelAccess = pricingRoot.model_access ?? getByPath(window.translations?.en?.pricing, 'model_access') ?? {};
  if (lane === 'max') {
    return {
      title: modelAccess.max_title ?? 'Max models are included with Premium.',
      subtitle: modelAccess.max_subtitle ?? 'Upgrade to Premium to chat with Max models.',
    };
  }
  return {
    title: modelAccess.plus_title ?? 'Plus models are included with Starter.',
    subtitle: modelAccess.plus_subtitle ?? 'Start a paid plan to chat with Plus models.',
  };
};

const applyModelAccessCopy = (modalContainer, { lane, lang }) => {
  const { title, subtitle } = getModelAccessStrings(lang, lane);
  applyModalTitleSubtitle(modalContainer, { title, subtitle, removeDisclaimer: true });
};

/**
 * Upsell when a locked Plus/Max response style is clicked.
 * Guest → free-account; Plus on free → trial/starter; Max → Premium.
 */
export const showModelAccessRequiredModal = async ({ lane = 'plus' } = {}) => {
  const accessLane = lane === 'max' ? 'max' : 'plus';
  try {
    if (window.__quotaModalOpen) return true;
    window.__quotaModalOpen = true;

    const lang = getCurrentLanguage();
    await loadTranslations('pricing', lang);
    const copy = getModelAccessStrings(lang, accessLane);

    if (!isLoggedInUser()) {
      const { showFreeAccountModal } = await import('../components/FreeAccountModal.js');
      await showFreeAccountModal(accessLane === 'max' ? 'max-models' : 'plus-models', copy);
      return true;
    }

    const userData = storage.get('userData') || {};
    const currentTier = String(userData?.current_tier || 'free').trim().toLowerCase();

    if (accessLane === 'plus') {
      if (currentTier !== 'free') {
        window.__quotaModalOpen = false;
        return false;
      }
      if (!isSfwSite()) {
        await showCryptoPacksModal();
        return true;
      }
      const hasBoughtTrial = userData.boughtTrial === true;
      const modalContainer = await mountPricingModal({
        template: hasBoughtTrial
          ? '/components/modals/starter-tier-modal.html'
          : '/components/modals/trial-offer-modal.html',
        afterMount: async (el, modalLang) => {
          applyModelAccessCopy(el, { lane: 'plus', lang: modalLang });
          setupTrialOfferModalEvents(el);
        },
      });
      return !!modalContainer;
    }

    if (PREMIUM_STYLE_TIERS.has(currentTier)) {
      window.__quotaModalOpen = false;
      return false;
    }

    const modalContainer = await mountPricingModal({
      template: '/components/modals/pricing-modal.html',
      prepare: (el) => {
        el.querySelectorAll('[data-pricing-plan]').forEach((planEl) => {
          if (planEl.getAttribute('data-pricing-plan') !== 'single') {
            planEl.remove();
          }
        });
      },
      afterMount: (el, modalLang) => {
        applyModelAccessCopy(el, { lane: 'max', lang: modalLang });
        wirePricingModalCheckout(el);
      },
    });
    return !!modalContainer;
  } catch (error) {
    console.error('[Model Access Modal] Error showing modal:', error);
    return false;
  } finally {
    try { window.__quotaModalOpen = false; } catch {}
  }
};

const showMonthlyLimitToast = async () => {
  const { showError } = await import('./UnifiedNotificationSystem.js');
  let message = "You've reached your monthly AI usage limit. It resets at the start of next month.";
  try {
    const lang = getCurrentLanguage();
    await loadTranslations('pricing', lang);
    const translated =
      window.translations?.[lang]?.pricing?.quota_exhausted?.monthly_limit_reached
      ?? window.translations?.en?.pricing?.quota_exhausted?.monthly_limit_reached;
    if (translated) message = translated;
  } catch {}
  showError(message, { duration: 6000 });
};

const showPaidTierQuotaUpsellModal = async ({ feature = 'ai' } = {}) => {
  try {
    const userData = storage.get('userData') || {};
    const currentTier = userData?.current_tier || 'free';
    const nextUiPlan = resolveNextUpgradeUiPlan(currentTier);
    if (!nextUiPlan) return false;

    const isStarterUpsell = nextUiPlan === 'starter';
    const modalContainer = await mountPricingModal({
      template: isStarterUpsell
        ? '/components/modals/starter-tier-modal.html'
        : '/components/modals/pricing-modal.html',
      initGrid: isStarterUpsell,
      prepare: isStarterUpsell
        ? undefined
        : (el) => {
          el.querySelectorAll('[data-pricing-plan]').forEach((planEl) => {
            if (planEl.getAttribute('data-pricing-plan') !== nextUiPlan) {
              planEl.remove();
            }
          });
        },
      afterMount: (el, lang) => {
        applyQuotaUpsellCopy(el, { feature, nextUiPlan, lang });
        el.querySelector('.pricing-messages-disclaimer')?.remove();
        if (isStarterUpsell) setupTrialOfferModalEvents(el);
        else wirePricingModalCheckout(el);
      },
    });
    return !!modalContainer;
  } catch (error) {
    console.error('[Quota Upsell Modal] Error showing modal:', error);
    return false;
  }
};

/**
 * Auth/tier gate for paid-only features (no API error required).
 * Guest → free-account modal; free + trial-eligible → trial-offer; free + boughtTrial → starter-tier; paid → false.
 */
export const showPaidRequiredModal = async ({
  context = 'unknown',
  applyBrowseCopy = false,
} = {}) => {
  try {
    if (window.__quotaModalOpen) return true;
    window.__quotaModalOpen = true;

    if (!isLoggedInUser()) {
      const { showFreeAccountModal } = await import('../components/FreeAccountModal.js');
      await showFreeAccountModal(context);
      return true;
    }

    const userData = storage.get('userData') || {};
    const currentTier = userData?.current_tier || 'free';
    if (currentTier !== 'free') {
      window.__quotaModalOpen = false;
      return false;
    }

    if (!isSfwSite()) {
      await showCryptoPacksModal();
      return true;
    }
    const hasBoughtTrial = userData.boughtTrial === true;
    const variant = hasBoughtTrial ? 'starter' : 'trial';
    const modalContainer = await mountPricingModal({
      template: hasBoughtTrial
        ? '/components/modals/starter-tier-modal.html'
        : '/components/modals/trial-offer-modal.html',
      afterMount: async (el, lang) => {
        if (applyBrowseCopy) await applyBrowseAccessCopy(el, variant, lang);
        setupTrialOfferModalEvents(el);
      },
    });
    return !!modalContainer;
  } catch (error) {
    console.error('[Paid Required Modal] Error showing modal:', error);
    return false;
  } finally {
    try { window.__quotaModalOpen = false; } catch {}
  }
};

export const showBrowsePaidRequiredModal = async () =>
  showPaidRequiredModal({ context: 'browse-url', applyBrowseCopy: true });

export const showGroupConversationRequiredModal = async ({
  context = 'group-conversations',
} = {}) => {
  try {
    if (window.__quotaModalOpen) return true;
    window.__quotaModalOpen = true;

    if (!isLoggedInUser()) {
      const { showFreeAccountModal } = await import('../components/FreeAccountModal.js');
      await showFreeAccountModal(context);
      return true;
    }

    const modalContainer = await mountPricingModal({
      template: '/components/modals/pricing-modal.html',
      prepare: (el) => {
        el.querySelectorAll('[data-pricing-plan]').forEach((planEl) => {
          if (planEl.getAttribute('data-pricing-plan') !== 'single') {
            planEl.remove();
          }
        });
      },
      afterMount: (el, lang) => {
        const nextTierName = formatTierDisplayName('single', lang);
        const { subtitle } = getQuotaUpsellStrings(lang, 'group');
        applyModalTitleSubtitle(el, {
          title: nextTierName,
          subtitle: interpolate(subtitle, { next_tier: nextTierName }),
        });
        el.querySelector('.pricing-messages-disclaimer')?.remove();
        wirePricingModalCheckout(el);
      },
    });
    return !!modalContainer;
  } catch (error) {
    console.error('[Group Conversation Modal] Error showing modal:', error);
    return false;
  } finally {
    try { window.__quotaModalOpen = false; } catch {}
  }
};

export const handleQuotaError = async (errorData, feature = 'messages') => {
  try {
    const isPaidRequired = errorData?.code === 'paid_required';
    const isStructuredQuota = errorData?.code === 'quota_exceeded' || errorData?.code === 'buzz_blocked';
    const text = (typeof errorData === 'string'
      ? errorData
      : `${errorData?.message || ''} ${errorData?.error || ''}`).toLowerCase();
    const signals = [
      'quota', 'limit', 'rate', 'payment', 'upgrade',
      'plan', 'subscription', 'credit', 'credits', 'balance', 'exceed'
    ];
    const isQuotaLike = isPaidRequired || isStructuredQuota || signals.some((s) => text.includes(s));
    if (!isQuotaLike) return false;

    if (isPaidRequired && errorData?.feature === 'group_conversations') {
      if (window.__quotaModalOpen) return true;
      return showGroupConversationRequiredModal({ context: 'hangout-group-conversation' });
    }

    if (window.__quotaModalOpen) return true;
    window.__quotaModalOpen = true;

    if (!isLoggedInUser()) {
      const { showFreeAccountModal } = await import('../components/FreeAccountModal.js');
      showFreeAccountModal().finally(() => {
        try { window.__quotaModalOpen = false; } catch {}
      });
      return true;
    }

    const currentTier = storage.get('userData')?.current_tier || 'free';
    if (currentTier === 'free') {
      showQuotaExhaustedModal().finally(() => {
        try { window.__quotaModalOpen = false; } catch {}
      });
      return true;
    }

    if (isPaidRequired) {
      window.__quotaModalOpen = false;
      return false;
    }

    const upsellShown = await showPaidTierQuotaUpsellModal({ feature });
    if (!upsellShown) await showMonthlyLimitToast();
    window.__quotaModalOpen = false;
    return true;
  } catch {
    try { window.__quotaModalOpen = false; } catch {}
    return false;
  }
};

const injectQuotaExhaustedCharacterName = (modalContainer, lang) => {
  const titleElement = modalContainer.querySelector('.quota-exhausted-custom-title');
  const characterNamePlaceholder = modalContainer.querySelector('.character-name-placeholder');
  if (!titleElement || !characterNamePlaceholder) return;

  const characterName = resolveVoiceModalCharacterName(null);
  characterNamePlaceholder.textContent = characterName;
  const quotaTranslations =
    window.translations?.[lang]?.pricing?.quota_exhausted
    ?? window.translations?.en?.pricing?.quota_exhausted;
  if (quotaTranslations?.title_template) {
    titleElement.innerHTML = quotaTranslations.title_template.replace('{character}', characterName);
  }
};

export const showQuotaExhaustedModal = async () => {
  try {
    if (!isLoggedInUser()) {
      await ensureLoginModal();
      return;
    }

    await mountPricingModal({
      template: '/components/modals/starter-tier-modal.html',
      delayGrid: true,
      afterMount: async (modalContainer, lang) => {
        injectQuotaExhaustedCharacterName(modalContainer, lang);
        await startQuotaCountdown(modalContainer);
        setupTrialOfferModalEvents(modalContainer, {
          onClose: () => clearQuotaCountdown(modalContainer),
          afterRemove: () => {
            const userData = storage.get('userData') || {};
            if (!userData.boughtTrial) showTrialOfferOnlyModal();
          },
        });
      },
    });
  } catch (error) {
    console.error('[Quota Exhausted Modal] Error showing modal:', error);
  }
};

const showTrialOfferOnlyModal = async () => {
  try {
    if (!isSfwSite() || !isLoggedInUser()) return;

    await mountPricingModal({
      template: '/components/modals/trial-offer-only-modal.html',
      delayGrid: true,
      closeDelayMs: 1000,
      afterMount: async (modalContainer) => {
        await startQuotaCountdown(modalContainer);
        setupTrialOfferModalEvents(modalContainer, {
          onClose: () => clearQuotaCountdown(modalContainer),
        });
      },
    });
  } catch (error) {
    console.error('[Trial Offer Only Modal] Error showing modal:', error);
  }
};
